import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import EquationInput from '../../../../components/inputs/EquationInput';

/* ===== Helpers ===== */

// sklej zapis współczynnika i części zmiennej z ukryciem "1"
function fmtTerm(coef, variable, pow) {
  if (coef === 0) return '0';
  const abs = Math.abs(coef);
  const sign = coef >= 0 ? '+' : '-';
  const varPart = pow === 0 ? '' : (pow === 1 ? variable : `${variable}^${pow}`);
  const coeffPart = (abs === 1 && varPart) ? '' : String(abs);
  return `${sign} ${coeffPart}${varPart}`.trim();
}
// to samo, ale bez znaku na początku (do pierwszego wyrazu)
function fmtTermHead(coef, variable, pow) {
  if (coef === 0) return '0';
  const abs = Math.abs(coef);
  const varPart = pow === 0 ? '' : (pow === 1 ? variable : `${variable}^${pow}`);
  const coeffPart = (abs === 1 && varPart) ? '' : String(abs);
  return `${coef < 0 ? '-' : ''}${coeffPart}${varPart}`.trim();
}
// prosty normalizator do porównań
function normalize(expr) {
  return String(expr || '')
    .replace(/−/g, '-')
    .replace(/\s+/g, '')
    .replace(/\*/g, '')
    .replace(/-/g, '+-')
    .toLowerCase()
    .split('+')
    .filter(Boolean)
    .sort()
    .join('+');
}
// losuje współczynnik z {±1,…,±9} bez zera
const randNZ = () => (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 9) + 1);

/* ===== GENERATE TASK ===== */
export function generateTask() {
  const variables = ['x', 'y', 'z'];
  const v = variables[Math.floor(Math.random() * variables.length)];

  // (a v + b), (c v^2 + d v + e) — wszystkie ≠ 0, znaki losowe
  const a = randNZ();   // przy v
  const b = randNZ();   // stała w 1. nawiasie
  const c = randNZ();   // przy v^2
  const d = randNZ();   // przy v
  const e = randNZ();   // wyraz wolny

  // ===== Budowa tekstu nawiasów (ukrywamy "1" przy zmiennych) =====
  const aAbs = Math.abs(a), bAbs = Math.abs(b);
  const cAbs = Math.abs(c), dAbs = Math.abs(d), eAbs = Math.abs(e);

  const aTerm = `${a < 0 ? '-' : ''}${aAbs === 1 ? '' : aAbs}${v}`;
  const firstBracket = `(${aTerm} ${b < 0 ? '-' : '+'} ${bAbs})`;

  const cTerm = `${c < 0 ? '-' : ''}${cAbs === 1 ? '' : cAbs}${v}^2`;
  const dTerm = `${d < 0 ? '-' : '+'} ${dAbs === 1 ? '' : dAbs}${v}`;
  const eTerm = `${e < 0 ? '-' : '+'} ${eAbs}`;
  const secondBracket = `(${cTerm} ${dTerm} ${eTerm})`;

  const expression = `${firstBracket}${secondBracket}`;

  // ===== Rozwinięcie „surowe” (przed redukcją) =====
  const rawTerms = [];
  const push = (coef, pow) => { if (coef !== 0) rawTerms.push({ coef, pow }); };

  push(a * c, 3);
  push(a * d, 2);
  push(a * e, 1);
  push(b * c, 2);
  push(b * d, 1);
  push(b * e, 0);

  // linia środkowa jako suma (z ukryciem "1")
  const termsStr = rawTerms.map(({ coef, pow }, i) =>
    (i === 0 ? fmtTermHead(coef, v, pow) : fmtTerm(coef, v, pow))
  );
  const rawExpanded = termsStr.join(' ')
    .replace(/\s\+\s-/g, ' - ')
    .replace(/\s\+\s\+/g, ' + ');

  // ===== Redukcja =====
  const grouped = {};
  for (const { coef, pow } of rawTerms) grouped[pow] = (grouped[pow] || 0) + coef;
  const powers = Object.keys(grouped).map(Number).sort((a, b) => b - a);

  let expected = '';
  powers.forEach((p, idx) => {
    const coef = grouped[p];
    if (coef === 0) return;
    const piece = (idx === 0 ? fmtTermHead : fmtTerm)(coef, v, p);
    expected = expected ? `${expected} ${piece}` : piece;
  });
  expected = expected.replace(/\s\+\s-/g, ' - ').replace(/\s\+\s\+/g, ' + ');

  return {
    type: 'MultiplyBinomialByTrinomialTask',
    data: { expression, expected, rawExpanded, termsStr }
  };
}

/* === Polecenie: desktop = jedna linia; mobile = formuła niżej i wyśrodkowana === */
export function renderTask({ data }) {
  return (
    <div className="cmd-container">
      <style>{`
        .cmd-container { margin-bottom: 14px; }     /* odstęp nad przyciskiem "Pokaż odpowiedź" */
        .cmd-wide { display: inline; }
        .cmd-narrow { display: none; }
        @media (max-width: 880px) {
          .cmd-container { margin-bottom: 18px; }   /* na telefonie trochę więcej */
          .cmd-wide { display: none; }
          .cmd-narrow { display: block; }
        }
      `}</style>

      {/* Desktop — w jednej linii */}
      <p className="cmd-wide">
        Uprość wyrażenie: <InlineMath math={data.expression} />
      </p>

      {/* Mobile — formuła pod tekstem, wyśrodkowana */}
      <div className="cmd-narrow">
        <p>Uprość wyrażenie:</p>
        <BlockMath math={data.expression} />
      </div>
    </div>
  );
}

/* ===== INPUT ===== */
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <EquationInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      placeholder='np. 2x^3 + 3x^2 + 4x + 5'
    />
  );
}

/* ===== CHECK ANSWER ===== */
export function checkAnswer({ data }, userAnswer) {
  return {
    isCorrect: normalize(userAnswer) === normalize(data.expected),
    expected: data.expected
  };
}

/* ===== Odpowiedź: desktop — inline; mobile — BlockMath ===== */
export function renderAnswer({ data }) {
  return (
    <>
      <style>{`
        .ans-wide { display: inline; }
        .ans-narrow { display: none; }
        @media (max-width: 880px) {
          .ans-wide { display: none; }
          .ans-narrow { display: block; }
        }
      `}</style>

      {/* Desktop */}
      <span className='ans-wide'>
        <InlineMath math={data.expected} />
      </span>

      {/* Mobile */}
      <span className='ans-narrow'>
        <BlockMath math={data.expected} />
      </span>
    </>
  );
}

/* ===== EXPLANATION =====
   Telefon:
     - 1) ( ... ) =
     - 2) +  pierwsza_część  +      ← kończy się „+”
     - 3) ±  ostatnie_dwa_wyrazy    ← zaczyna się znakiem przedostatniego
   Desktop:
     - Krok 1 i 2 — po jednej linii.
*/
export function renderExplanation({ data }) {
  const { expression, rawExpanded, expected, termsStr } = data;

  // Desktop — jedna linia
  const step1Single = String.raw`${expression} \;=\; ${rawExpanded}`;

  // ===== Mobile: przenieś DWA OSTATNIE wyrazy do nowej linii =====
  // Z parsowania "termsStr" znamy każde "± term" (pierwszy bez wiodącego "+").
  // Warunek łamania: jeśli suma ma >= 6 wyrazów LUB długość > próg.
  const NEEDS_WRAP = termsStr.length >= 6 || rawExpanded.length > 40;

  let step1Wrapped;
  if (NEEDS_WRAP) {
    const n = termsStr.length;
    const firstPart = termsStr.slice(0, n - 2).join(' ')
      .replace(/\s\+\s-/g, ' - ')
      .replace(/\s\+\s\+/g, ' + ');
    // ostatnie dwa z ich rzeczywistymi znakami
    const penult = termsStr[n - 2]; // np. "+ 16z" albo "- 16z"
    const last   = termsStr[n - 1]; // np. "+ 12"  albo "- 12"

    // linia 2 kończy się na '+'
    const line2 = `${firstPart} \\; +`;

    // linia 3 zaczyna się znakiem przedostatniego
    const penultSign = /^\-/.test(penult) ? '-' : '+';
    const penultBody = penult.replace(/^[-+]\s*/, ''); // bez znaku
    const lastSign   = /^\-/.test(last) ? '-' : '+';
    const lastBody   = last.replace(/^[-+]\s*/, '');

    const line3 = `${penultSign}\\; ${penultBody} \\; ${lastSign} \\; ${lastBody}`;

    step1Wrapped = String.raw`
\begin{array}{c}
${expression}\; = \\\\
+\; ${line2.replace(/^\+\;\s*/, '')} \\\\
${line3}
\end{array}
`;
  } else {
    // krótka suma — dwie linie z "=" (jak wcześniej)
    step1Wrapped = String.raw`
\begin{array}{c}
${expression}\; = \\\\
=\; ${rawExpanded}
\end{array}
`;
  }

  return (
    <div className='exp-wrap' style={{ lineHeight: 1.7 }}>
      <style>{`
        .exp-wrap p { text-align: left; }
        .exp-wrap .katex-display { text-align: center !important; } /* wyśrodkuj bloki KaTeX */
        .exp-wide { display: block; }
        .exp-narrow { display: none; }
        @media (max-width: 880px) {
          .exp-wide { display: none; }
          .exp-narrow { display: block; }
        }
      `}</style>

      <p><strong>Krok 1:</strong> Rozwijamy nawiasy (mnożymy każdy składnik przez każdy):</p>

      {/* Desktop */}
      <div className='exp-wide'>
        <BlockMath math={step1Single} />
      </div>

      {/* Mobile — łamanie: linia 2 kończy się „+”, linia 3 zaczyna rzeczywistym znakiem */}
      <div className='exp-narrow'>
        <BlockMath math={step1Wrapped} />
      </div>

      <p><strong>Krok 2:</strong> Sumujemy i upraszczamy wyrazy podobne:</p>
      <BlockMath math={expected} />
    </div>
  );
}

/* ===== USER ANSWER ===== */
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === 'string' ? userAnswer : userAnswer?.raw ?? '';
  if (!raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={raw} />;
}
